/**
 * Logging Service
 *
 * Provides structured logging with:
 * - Log levels (debug, info, warn, error)
 * - Request correlation IDs
 * - Performance timing
 * - Environment-aware formatting (dev vs prod)
 * - Debug mode toggle
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogContext = Record<string, unknown>;

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  correlationId?: string;
  service?: string;
  duration?: number;
}

// Configuration
let debugMode = import.meta.env.DEV; // Enable debug logs in development by default
let minLogLevel: LogLevel = import.meta.env.DEV ? 'debug' : 'info';

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Correlation ID tracking for request tracing
let currentCorrelationId: string | undefined;

/**
 * Generate a unique correlation ID for request tracking
 */
export const generateCorrelationId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Set correlation ID for the current request/operation
 */
export const setCorrelationId = (id: string): void => {
  currentCorrelationId = id;
};

/**
 * Clear the current correlation ID
 */
export const clearCorrelationId = (): void => {
  currentCorrelationId = undefined;
};

/**
 * Get the current correlation ID
 */
export const getCorrelationId = (): string | undefined => {
  return currentCorrelationId;
};

/**
 * Enable or disable debug mode
 */
export const setDebugMode = (enabled: boolean): void => {
  debugMode = enabled;
  minLogLevel = enabled ? 'debug' : 'info';
  log('info', 'LoggingService', `Debug mode ${enabled ? 'enabled' : 'disabled'}`);
};

/**
 * Check if debug mode is enabled
 */
export const isDebugMode = (): boolean => {
  return debugMode;
};

/**
 * Set minimum log level
 */
export const setMinLogLevel = (level: LogLevel): void => {
  minLogLevel = level;
};

/**
 * Check if a log level should be logged based on current minimum level
 */
const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[minLogLevel];
};

/**
 * Format log entry for console output
 */
const formatForConsole = (entry: LogEntry): string => {
  const timestamp = new Date(entry.timestamp).toISOString();
  const level = entry.level.toUpperCase().padEnd(5);
  const service = entry.service ? `[${entry.service}]` : '';
  const correlationId = entry.correlationId ? `[${entry.correlationId}]` : '';
  const duration = entry.duration !== undefined ? ` (${entry.duration}ms)` : '';

  let message = `${timestamp} ${level} ${service}${correlationId} ${entry.message}${duration}`;

  if (entry.context && Object.keys(entry.context).length > 0) {
    message += `\n  Context: ${JSON.stringify(entry.context, null, 2)}`;
  }

  return message;
};

/**
 * Core logging function
 */
export const log = (
  level: LogLevel,
  service: string,
  message: string,
  context?: LogContext,
  duration?: number
): void => {
  if (!shouldLog(level)) {
    return;
  }

  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    context,
    correlationId: currentCorrelationId,
    service,
    duration,
  };

  // In development, use formatted console output
  if (import.meta.env.DEV) {
    const formattedMessage = formatForConsole(entry);

    switch (level) {
      case 'debug':
        // eslint-disable-next-line no-console
        console.debug(formattedMessage);
        break;
      case 'info':
        // eslint-disable-next-line no-console
        console.info(formattedMessage);
        break;
      case 'warn':
        // eslint-disable-next-line no-console
        console.warn(formattedMessage);
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.error(formattedMessage);
        break;
    }
  } else {
    // In production, use structured JSON logs
    const jsonLog = JSON.stringify(entry);

    switch (level) {
      case 'debug':
        // eslint-disable-next-line no-console
        console.debug(jsonLog);
        break;
      case 'info':
        // eslint-disable-next-line no-console
        console.info(jsonLog);
        break;
      case 'warn':
        // eslint-disable-next-line no-console
        console.warn(jsonLog);
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.error(jsonLog);
        break;
    }
  }
};

/**
 * Convenience logging functions
 */
export const debug = (service: string, message: string, context?: LogContext): void => {
  log('debug', service, message, context);
};

export const info = (service: string, message: string, context?: LogContext): void => {
  log('info', service, message, context);
};

export const warn = (service: string, message: string, context?: LogContext): void => {
  log('warn', service, message, context);
};

export const error = (service: string, message: string, context?: LogContext): void => {
  log('error', service, message, context);
};

/**
 * Create a logger instance for a specific service
 */
export class Logger {
  constructor(private serviceName: string) {}

  debug(message: string, context?: LogContext): void {
    debug(this.serviceName, message, context);
  }

  info(message: string, context?: LogContext): void {
    info(this.serviceName, message, context);
  }

  warn(message: string, context?: LogContext): void {
    warn(this.serviceName, message, context);
  }

  error(message: string, context?: LogContext): void {
    error(this.serviceName, message, context);
  }

  /**
   * Start a performance timer
   */
  startTimer(operation: string): () => void {
    const startTime = performance.now();
    this.debug(`Starting: ${operation}`);

    return () => {
      const duration = Math.round(performance.now() - startTime);
      this.info(`Completed: ${operation}`, { duration });
    };
  }

  /**
   * Log with correlation ID
   */
  withCorrelationId<T>(fn: () => T): T {
    const correlationId = generateCorrelationId();
    setCorrelationId(correlationId);

    try {
      this.debug('Operation started', { correlationId });
      const result = fn();
      this.debug('Operation completed', { correlationId });
      return result;
    } finally {
      clearCorrelationId();
    }
  }

  /**
   * Log async operation with correlation ID
   */
  async withCorrelationIdAsync<T>(fn: () => Promise<T>): Promise<T> {
    const correlationId = generateCorrelationId();
    setCorrelationId(correlationId);

    try {
      this.debug('Async operation started', { correlationId });
      const result = await fn();
      this.debug('Async operation completed', { correlationId });
      return result;
    } finally {
      clearCorrelationId();
    }
  }
}

/**
 * Create a logger for a specific service
 */
export const createLogger = (serviceName: string): Logger => {
  return new Logger(serviceName);
};

/**
 * Performance tracing decorator for functions
 */
export const trace = <T extends (...args: unknown[]) => unknown>(
  serviceName: string,
  operation: string,
  fn: T
): T => {
  return ((...args: unknown[]) => {
    const startTime = performance.now();
    const correlationId = currentCorrelationId || generateCorrelationId();

    debug(serviceName, `[${operation}] Starting`, { correlationId });

    try {
      const result = fn(...args);

      // Handle async functions
      if (result instanceof Promise) {
        return result
          .then((value) => {
            const duration = Math.round(performance.now() - startTime);
            info(serviceName, `[${operation}] Completed`, { correlationId, duration });
            return value;
          })
          .catch((err) => {
            const duration = Math.round(performance.now() - startTime);
            error(serviceName, `[${operation}] Failed`, {
              correlationId,
              duration,
              error: err instanceof Error ? err.message : String(err),
            });
            throw err;
          });
      }

      // Synchronous function
      const duration = Math.round(performance.now() - startTime);
      info(serviceName, `[${operation}] Completed`, { correlationId, duration });
      return result;
    } catch (err) {
      const duration = Math.round(performance.now() - startTime);
      error(serviceName, `[${operation}] Failed`, {
        correlationId,
        duration,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  }) as T;
};

/**
 * Measure performance of a code block
 */
export const measurePerformance = async <T>(
  serviceName: string,
  operation: string,
  fn: () => Promise<T> | T
): Promise<T> => {
  const startTime = performance.now();
  const correlationId = currentCorrelationId || generateCorrelationId();

  debug(serviceName, `[${operation}] Starting`, { correlationId });

  try {
    const result = await fn();
    const duration = Math.round(performance.now() - startTime);

    // Warn if operation is slow
    if (duration > 1000) {
      warn(serviceName, `[${operation}] Slow operation detected`, { correlationId, duration });
    } else {
      info(serviceName, `[${operation}] Completed`, { correlationId, duration });
    }

    return result;
  } catch (err) {
    const duration = Math.round(performance.now() - startTime);
    error(serviceName, `[${operation}] Failed`, {
      correlationId,
      duration,
      error: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
};

// Export a default logger instance
export const logger = createLogger('App');
